import { useCallback, useEffect, useState } from 'react'
import { Activity, Edit as EditIcon, Power, PowerOff, RefreshCw, Trash2, UserPlus } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog'
import { apiClient } from '@/lib/api'
import { useToast } from '@/hooks/use-toast'

interface SystemSettings {
  environment: string
  debug?: boolean
  log_level: string
  access_token_expire_minutes: number
  refresh_token_expire_days: number
  azure_openai_deployment_name: string
  report_logo_url: string
}

interface SystemRule {
  id: string
  name: string
  description?: string
  keywords: string[]
  handles: string[]
  domains: string[]
  platforms: string[]
  sentiment_filter?: string
  created_at: string
  created_by: string
}

interface MonitorStatus {
  monitor_enabled: boolean
  scheduler_running: boolean
  ingestor_status: Record<string, any>
}

interface AdminSettings {
  registration_enabled?: boolean
  monitor_enabled?: boolean
}

interface RuleFormState {
  name: string
  description: string
  keywords: string[]
  handles: string[]
  domains: string[]
  platforms: string[]
  sentiment_filter: string
}

const EMPTY_RULE_FORM: RuleFormState = {
  name: '',
  description: '',
  keywords: [],
  handles: [],
  domains: [],
  platforms: [],
  sentiment_filter: '',
}

function parseCsv(value: string): string[] {
  return value.split(',').map((s) => s.trim()).filter(Boolean)
}

// Only send fields that actually changed from the rule's current values, per
// the PUT /system/rules/{rule_id} contract (PrecreatedRuleUpdate — every
// field optional, unset fields are left untouched server-side).
function diffRuleForm(original: SystemRule, form: RuleFormState): Partial<RuleFormState> {
  const changed: Partial<RuleFormState> = {}
  if (form.name !== original.name) changed.name = form.name
  if (form.description !== (original.description || '')) changed.description = form.description
  if (JSON.stringify(form.keywords) !== JSON.stringify(original.keywords || [])) changed.keywords = form.keywords
  if (JSON.stringify(form.handles) !== JSON.stringify(original.handles || [])) changed.handles = form.handles
  if (JSON.stringify(form.domains) !== JSON.stringify(original.domains || [])) changed.domains = form.domains
  if (JSON.stringify(form.platforms) !== JSON.stringify(original.platforms || [])) changed.platforms = form.platforms
  if (form.sentiment_filter !== (original.sentiment_filter || '')) changed.sentiment_filter = form.sentiment_filter
  return changed
}

// Admin Panel Redesign — ported from the old AdminPanel.tsx "system" tab:
// Platform Settings (registration toggle), Monitor Dashboard Service
// start/stop, System Settings (editable values), and Monitoring Rules.
// Fetches everything itself on mount instead of sharing state/loadData()
// with the rest of the old monolithic panel.
//
// The Email/SMTP configuration card that used to live in this same tab has
// moved out to its own "Emails" section (EmailsSection.tsx) — intentionally
// not duplicated here.
//
// Bug fix: the old Delete button called `(apiClient as any).deletePrecreatedRule`
// — cast through `as any` because the method didn't exist on apiClient, so
// every click threw at runtime (silently swallowed into a failure toast).
// apiClient now has real `deletePrecreatedRule`/`updatePrecreatedRule`
// methods backed by working PUT/DELETE /system/rules/{rule_id} routes.
// Delete now calls `apiClient.deletePrecreatedRule` directly (no `as any`),
// confirmed via an AlertDialog instead of a bare `confirm()`, and a new Edit
// dialog (reusing the create-rule form fields, pre-filled) was added
// alongside it.
export default function SystemRulesSection() {
  const { toast } = useToast()

  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState<string | null>(null)

  const [adminSettings, setAdminSettings] = useState<AdminSettings | null>(null)
  const [registrationLoading, setRegistrationLoading] = useState(false)

  const [monitorStatus, setMonitorStatus] = useState<MonitorStatus | null>(null)
  const [monitorActionLoading, setMonitorActionLoading] = useState(false)

  const [systemSettings, setSystemSettings] = useState<SystemSettings | null>(null)
  const [editingSetting, setEditingSetting] = useState<string | null>(null)
  const [settingValue, setSettingValue] = useState<any>('')

  const [rules, setRules] = useState<SystemRule[]>([])
  const [rulesLoading, setRulesLoading] = useState(false)

  const [showCreateRuleDialog, setShowCreateRuleDialog] = useState(false)
  const [newRule, setNewRule] = useState<RuleFormState>(EMPTY_RULE_FORM)
  const [creatingRule, setCreatingRule] = useState(false)

  const [editingRule, setEditingRule] = useState<SystemRule | null>(null)
  const [editRuleForm, setEditRuleForm] = useState<RuleFormState>(EMPTY_RULE_FORM)
  const [savingRuleEdit, setSavingRuleEdit] = useState(false)

  const [deletingRuleId, setDeletingRuleId] = useState<string | null>(null)

  const loadAll = useCallback(async () => {
    setLoading(true)
    setLoadError(null)
    try {
      const [settings, rulesRes, admSettings, monStatus] = await Promise.all([
        apiClient.getSystemSettings(),
        apiClient.getPrecreatedRules(),
        apiClient.getAdminSettings().catch(() => null),
        apiClient.getMonitorServiceStatus().catch(() => null),
      ])
      setSystemSettings(settings as SystemSettings)
      setRules(((rulesRes as any)?.rules || []) as SystemRule[])
      setAdminSettings(admSettings)
      setMonitorStatus(monStatus)
    } catch (e: any) {
      console.error('Failed to load system data', e)
      setLoadError(e?.message || 'Failed to load system settings and rules')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    loadAll()
  }, [loadAll])

  const refreshRules = async () => {
    setRulesLoading(true)
    try {
      const rulesRes = await apiClient.getPrecreatedRules()
      setRules(((rulesRes as any)?.rules || []) as SystemRule[])
    } catch (e: any) {
      toast({ title: 'Failed to refresh rules', description: e?.message, variant: 'destructive' })
    } finally {
      setRulesLoading(false)
    }
  }

  const refreshMonitorStatus = async () => {
    try {
      const updated = await apiClient.getMonitorServiceStatus()
      setMonitorStatus(updated)
    } catch (e: any) {
      toast({ title: 'Failed to refresh monitor status', description: e?.message, variant: 'destructive' })
    }
  }

  const handleToggleRegistration = async () => {
    const next = adminSettings?.registration_enabled === false
    setRegistrationLoading(true)
    try {
      await apiClient.updateAdminSettings({ registration_enabled: next })
      setAdminSettings({ ...adminSettings, registration_enabled: next })
      toast({ title: next ? 'Registration enabled' : 'Registration disabled' })
    } catch (e: any) {
      toast({ title: 'Failed to update', description: e?.message, variant: 'destructive' })
    } finally {
      setRegistrationLoading(false)
    }
  }

  const handleToggleMonitor = async () => {
    if (!monitorStatus) return
    setMonitorActionLoading(true)
    try {
      if (monitorStatus.scheduler_running) {
        await apiClient.stopMonitorService()
        toast({ title: 'Monitor service stopped' })
      } else {
        await apiClient.startMonitorService()
        toast({ title: 'Monitor service started' })
      }
      const updated = await apiClient.getMonitorServiceStatus()
      setMonitorStatus(updated)
    } catch (e: any) {
      toast({ title: 'Failed to update monitor service', description: e?.message, variant: 'destructive' })
    } finally {
      setMonitorActionLoading(false)
    }
  }

  const handleUpdateSystemSetting = async () => {
    if (!editingSetting) return
    try {
      await apiClient.updateSystemSetting(editingSetting, settingValue)
      setEditingSetting(null)
      setSettingValue('')
      const settings = await apiClient.getSystemSettings()
      setSystemSettings(settings as SystemSettings)
      toast({ title: 'Setting updated' })
    } catch (e: any) {
      toast({ title: 'Failed to update setting', description: e?.message, variant: 'destructive' })
    }
  }

  const handleCreateRule = async () => {
    if (!newRule.name.trim()) {
      toast({ title: 'Rule name is required', variant: 'destructive' })
      return
    }
    setCreatingRule(true)
    try {
      await apiClient.createPrecreatedRule(newRule)
      setShowCreateRuleDialog(false)
      setNewRule(EMPTY_RULE_FORM)
      await refreshRules()
      toast({ title: 'Rule created' })
    } catch (e: any) {
      toast({ title: 'Failed to create rule', description: e?.message, variant: 'destructive' })
    } finally {
      setCreatingRule(false)
    }
  }

  const openEditRule = (rule: SystemRule) => {
    setEditingRule(rule)
    setEditRuleForm({
      name: rule.name,
      description: rule.description || '',
      keywords: rule.keywords || [],
      handles: rule.handles || [],
      domains: rule.domains || [],
      platforms: rule.platforms || [],
      sentiment_filter: rule.sentiment_filter || '',
    })
  }

  const handleUpdateRule = async () => {
    if (!editingRule) return
    if (!editRuleForm.name.trim()) {
      toast({ title: 'Rule name is required', variant: 'destructive' })
      return
    }
    const changed = diffRuleForm(editingRule, editRuleForm)
    if (Object.keys(changed).length === 0) {
      setEditingRule(null)
      return
    }
    setSavingRuleEdit(true)
    try {
      await apiClient.updatePrecreatedRule(editingRule.id, changed)
      setEditingRule(null)
      await refreshRules()
      toast({ title: 'Rule updated' })
    } catch (e: any) {
      toast({ title: 'Failed to update rule', description: e?.message, variant: 'destructive' })
    } finally {
      setSavingRuleEdit(false)
    }
  }

  const handleDeleteRule = async (ruleId: string) => {
    setDeletingRuleId(ruleId)
    try {
      await apiClient.deletePrecreatedRule(ruleId)
      await refreshRules()
      toast({ title: 'Rule deleted' })
    } catch (e: any) {
      toast({ title: 'Failed to delete rule', description: e?.message, variant: 'destructive' })
    } finally {
      setDeletingRuleId(null)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24 text-muted-foreground">
        <Activity className="w-5 h-5 mr-2 animate-spin" />
        Loading system settings...
      </div>
    )
  }

  if (loadError) {
    return (
      <Card>
        <CardContent className="py-10 text-center">
          <p className="text-sm text-destructive mb-4">{loadError}</p>
          <Button variant="outline" onClick={loadAll}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Retry
          </Button>
        </CardContent>
      </Card>
    )
  }

  const renderRuleFormFields = (form: RuleFormState, setForm: (f: RuleFormState) => void) => (
    <div className="space-y-4 mt-4">
      <div>
        <Label>Rule Name</Label>
        <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
      </div>
      <div>
        <Label>Description</Label>
        <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
      </div>
      <div>
        <Label>Keywords (comma-separated)</Label>
        <Input
          placeholder="keyword1, keyword2, keyword3"
          value={form.keywords.join(', ')}
          onChange={(e) => setForm({ ...form, keywords: parseCsv(e.target.value) })}
        />
      </div>
      <div>
        <Label>Handles (comma-separated)</Label>
        <Input
          placeholder="@handle1, @handle2"
          value={form.handles.join(', ')}
          onChange={(e) => setForm({ ...form, handles: parseCsv(e.target.value) })}
        />
      </div>
      <div>
        <Label>Domains (comma-separated)</Label>
        <Input
          placeholder="example.com, example.org"
          value={form.domains.join(', ')}
          onChange={(e) => setForm({ ...form, domains: parseCsv(e.target.value) })}
        />
      </div>
      <div>
        <Label>Platforms (comma-separated)</Label>
        <Input
          placeholder="twitter, reddit, instagram"
          value={form.platforms.join(', ')}
          onChange={(e) => setForm({ ...form, platforms: parseCsv(e.target.value) })}
        />
      </div>
      <div>
        <Label>Sentiment Filter</Label>
        <Input
          placeholder="positive, negative, neutral"
          value={form.sentiment_filter}
          onChange={(e) => setForm({ ...form, sentiment_filter: e.target.value })}
        />
      </div>
    </div>
  )

  return (
    <div className="space-y-4">
      {/* Platform Settings */}
      <Card>
        <CardHeader>
          <CardTitle>Platform Settings</CardTitle>
          <p className="text-sm text-muted-foreground mt-1">Control registration and access</p>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between p-4 border border-border rounded">
            <div>
              <Label className="text-base font-medium">Registration</Label>
              <p className="text-sm text-muted-foreground mt-1">
                Allow new users to register. When disabled, only admins can create accounts or invite users.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">
                {adminSettings?.registration_enabled !== false ? 'Enabled' : 'Disabled'}
              </span>
              <Button
                variant={adminSettings?.registration_enabled !== false ? 'default' : 'outline'}
                size="sm"
                disabled={registrationLoading}
                onClick={handleToggleRegistration}
              >
                {adminSettings?.registration_enabled !== false ? 'Disable' : 'Enable'}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Monitor Dashboard Service Control */}
      <Card>
        <CardHeader>
          <CardTitle>Monitor Dashboard Service</CardTitle>
          <p className="text-sm text-muted-foreground mt-1">
            Control the background ingestion scheduler (GDELT, USGS, CISA KEV, Ransomware Live, NASA FIRMS, FalconFeeds).
            Changes take effect immediately and persist across restarts.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          {monitorStatus ? (
            <>
              <div className="flex items-center justify-between p-4 border border-border rounded">
                <div>
                  <Label className="text-base font-medium">Ingestion Scheduler</Label>
                  <p className="text-sm text-muted-foreground mt-1">
                    {monitorStatus.scheduler_running
                      ? 'Running — actively polling all data sources'
                      : 'Stopped — no data is being ingested'}
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <div className={`flex items-center gap-1.5 text-sm font-medium ${monitorStatus.scheduler_running ? 'text-green-500' : 'text-red-400'}`}>
                    <span className={`w-2 h-2 rounded-full ${monitorStatus.scheduler_running ? 'bg-green-500 animate-pulse' : 'bg-red-400'}`} />
                    {monitorStatus.scheduler_running ? 'Running' : 'Stopped'}
                  </div>
                  <Button
                    size="sm"
                    variant={monitorStatus.scheduler_running ? 'destructive' : 'default'}
                    disabled={monitorActionLoading}
                    onClick={handleToggleMonitor}
                  >
                    {monitorActionLoading ? (
                      <Activity className="w-4 h-4 animate-spin" />
                    ) : monitorStatus.scheduler_running ? (
                      <>
                        <PowerOff className="w-4 h-4 mr-1" />
                        Stop
                      </>
                    ) : (
                      <>
                        <Power className="w-4 h-4 mr-1" />
                        Start
                      </>
                    )}
                  </Button>
                  <Button size="sm" variant="ghost" disabled={monitorActionLoading} onClick={refreshMonitorStatus} title="Refresh status">
                    <RefreshCw className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              {Object.keys(monitorStatus.ingestor_status).length > 0 && (
                <div>
                  <Label className="text-xs text-muted-foreground mb-2 block">Ingestor Status</Label>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    {Object.entries(monitorStatus.ingestor_status).map(([name, info]: [string, any]) => (
                      <div key={name} className="p-2 border border-border rounded text-xs">
                        <div className="flex items-center justify-between mb-1">
                          <span className="font-semibold capitalize">{name.replace('_', ' ')}</span>
                          <span
                            className={`w-2 h-2 rounded-full ${
                              info.status === 'ok' || info.status === 'running'
                                ? 'bg-green-500'
                                : info.status === 'error'
                                ? 'bg-red-500'
                                : 'bg-yellow-500'
                            }`}
                          />
                        </div>
                        {info.last_run && <p className="text-muted-foreground">Last: {new Date(info.last_run).toLocaleTimeString()}</p>}
                        {info.events_ingested !== undefined && <p className="text-muted-foreground">{info.events_ingested} events</p>}
                        {info.error && (
                          <p className="text-red-400 truncate" title={info.error}>
                            {info.error}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-6 text-muted-foreground">
              <Activity className="w-8 h-8 mx-auto mb-2 opacity-40" />
              <p className="text-sm">Monitor status unavailable</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* System Settings */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>System Settings</CardTitle>
          <Button variant="outline" onClick={loadAll}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </CardHeader>
        <CardContent>
          {systemSettings ? (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="p-4 border border-border rounded">
                  <Label className="text-sm text-muted-foreground">Environment</Label>
                  <p className="text-lg font-semibold">{systemSettings.environment}</p>
                </div>
                <div className="p-4 border border-border rounded">
                  <Label className="text-sm text-muted-foreground">Log Level</Label>
                  <p className="text-lg font-semibold">{systemSettings.log_level}</p>
                </div>
                <div className="p-4 border border-border rounded">
                  <Label className="text-sm text-muted-foreground">Access Token Expiry (minutes)</Label>
                  <div className="flex items-center gap-2">
                    <p className="text-lg font-semibold">{systemSettings.access_token_expire_minutes}</p>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => {
                        setEditingSetting('access_token_expire_minutes')
                        setSettingValue(systemSettings.access_token_expire_minutes)
                      }}
                    >
                      <EditIcon className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
                <div className="p-4 border border-border rounded">
                  <Label className="text-sm text-muted-foreground">Refresh Token Expiry (days)</Label>
                  <div className="flex items-center gap-2">
                    <p className="text-lg font-semibold">{systemSettings.refresh_token_expire_days}</p>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => {
                        setEditingSetting('refresh_token_expire_days')
                        setSettingValue(systemSettings.refresh_token_expire_days)
                      }}
                    >
                      <EditIcon className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
                <div className="p-4 border border-border rounded">
                  <Label className="text-sm text-muted-foreground">Azure OpenAI Deployment</Label>
                  <p className="text-lg font-semibold">{systemSettings.azure_openai_deployment_name}</p>
                </div>
                <div className="p-4 border border-border rounded">
                  <Label className="text-sm text-muted-foreground">Report Logo URL</Label>
                  <div className="flex items-center gap-2">
                    <p className="text-sm truncate">{systemSettings.report_logo_url}</p>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => {
                        setEditingSetting('report_logo_url')
                        setSettingValue(systemSettings.report_logo_url)
                      }}
                    >
                      <EditIcon className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
              </div>
              {systemSettings.debug !== undefined && (
                <div className="p-4 border border-border rounded">
                  <Label className="text-sm text-muted-foreground">Debug Mode</Label>
                  <p className="text-lg font-semibold">{systemSettings.debug ? 'Enabled' : 'Disabled'}</p>
                </div>
              )}
            </div>
          ) : (
            <p className="text-muted-foreground">Loading settings...</p>
          )}
        </CardContent>
      </Card>

      {/* Monitoring Rules */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Monitoring Rules</CardTitle>
          <Dialog
            open={showCreateRuleDialog}
            onOpenChange={(open) => {
              setShowCreateRuleDialog(open)
              if (!open) setNewRule(EMPTY_RULE_FORM)
            }}
          >
            <DialogTrigger asChild>
              <Button>
                <UserPlus className="w-4 h-4 mr-2" />
                Create Rule
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Create Monitoring Rule</DialogTitle>
              </DialogHeader>
              {renderRuleFormFields(newRule, setNewRule)}
              <Button onClick={handleCreateRule} disabled={creatingRule} className="w-full">
                {creatingRule ? 'Creating...' : 'Create Rule'}
              </Button>
            </DialogContent>
          </Dialog>
        </CardHeader>
        <CardContent>
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {rulesLoading && <p className="text-sm text-muted-foreground">Refreshing...</p>}
            {rules.map((rule) => (
              <div key={rule.id} className="p-4 border border-border rounded">
                <div className="flex justify-between items-start">
                  <div className="flex-1 min-w-0">
                    <h4 className="font-semibold">{rule.name}</h4>
                    {rule.description && <p className="text-sm text-muted-foreground mt-1">{rule.description}</p>}
                    <div className="mt-2 flex flex-wrap gap-2">
                      {rule.keywords.length > 0 && (
                        <div>
                          <span className="text-xs font-semibold">Keywords: </span>
                          {rule.keywords.slice(0, 3).map((k, i) => (
                            <Badge key={i} variant="outline" className="mr-1">
                              {k}
                            </Badge>
                          ))}
                          {rule.keywords.length > 3 && <span className="text-xs">+{rule.keywords.length - 3} more</span>}
                        </div>
                      )}
                      {rule.platforms.length > 0 && (
                        <div>
                          <span className="text-xs font-semibold">Platforms: </span>
                          {rule.platforms.map((p, i) => (
                            <Badge key={i} variant="outline" className="mr-1">
                              {p}
                            </Badge>
                          ))}
                        </div>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground/70 mt-2">
                      Created by {rule.created_by} on {new Date(rule.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="flex items-center gap-1 ml-2 shrink-0">
                    <Button size="sm" variant="ghost" onClick={() => openEditRule(rule)} title="Edit Rule">
                      <EditIcon className="w-4 h-4" />
                    </Button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-destructive hover:text-destructive hover:bg-destructive/10"
                          disabled={deletingRuleId === rule.id}
                          title="Delete Rule"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Delete monitoring rule?</AlertDialogTitle>
                          <AlertDialogDescription>
                            This will permanently delete &quot;{rule.name}&quot;. This action cannot be undone.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction destructive onClick={() => handleDeleteRule(rule.id)}>
                            Delete
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>
              </div>
            ))}
            {rules.length === 0 && !rulesLoading && <p className="text-muted-foreground text-center py-8">No rules created yet</p>}
          </div>
        </CardContent>
      </Card>

      {/* System Setting Update Dialog */}
      <Dialog open={!!editingSetting} onOpenChange={(open) => !open && setEditingSetting(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Update System Setting</DialogTitle>
            <p className="text-sm text-muted-foreground mt-1">Setting: {editingSetting}</p>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label>New Value</Label>
              <Input
                type={typeof settingValue === 'number' ? 'number' : 'text'}
                value={settingValue}
                onChange={(e) => {
                  const value = typeof settingValue === 'number' ? parseInt(e.target.value, 10) : e.target.value
                  setSettingValue(value)
                }}
              />
            </div>
            <div className="flex gap-2">
              <Button onClick={handleUpdateSystemSetting} className="flex-1">
                Update
              </Button>
              <Button variant="outline" onClick={() => setEditingSetting(null)}>
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Rule Dialog */}
      <Dialog open={!!editingRule} onOpenChange={(open) => !open && setEditingRule(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Monitoring Rule</DialogTitle>
          </DialogHeader>
          {renderRuleFormFields(editRuleForm, setEditRuleForm)}
          <div className="flex gap-2">
            <Button onClick={handleUpdateRule} disabled={savingRuleEdit} className="flex-1">
              {savingRuleEdit ? 'Saving...' : 'Save Changes'}
            </Button>
            <Button variant="outline" onClick={() => setEditingRule(null)}>
              Cancel
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}
